import { roleAtLeast, type SecurityEventSummary } from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { getEvent, updateEvent } from '../api/events.js';
import { useCurrentUser } from '../auth/session-context.js';
import { effectiveTimezone, formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Facts } from '../ui/DataTable.js';
import { Dialog } from '../ui/Dialog.js';
import { AddressLink } from './AddressStatus.js';
import { SeverityBadge } from './StatusBadge.js';

/**
 * One event, opened from the list (P11.7): what happened, where and from whom,
 * and the decision most events need — acknowledge it — without leaving the list.
 * Notes, assignment and asking AI stay on the event's own page, one click away.
 */
export function EventPanel({
  event,
  onClose,
}: {
  event: SecurityEventSummary;
  onClose: () => void;
}): ReactNode {
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const canAct = roleAtLeast(session.user.role, 'operator');
  const queryClient = useQueryClient();
  const detail = useQuery({ queryKey: ['event', event.id], queryFn: () => getEvent(event.id) });
  const current = detail.data ?? event;

  const acknowledge = useMutation({
    mutationFn: (value: boolean) => updateEvent(event.id, { acknowledged: value }),
    onSuccess: (next) => {
      queryClient.setQueryData(['event', event.id], next);
      void queryClient.invalidateQueries({ queryKey: ['events'] });
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
    },
  });

  const gap = Date.parse(current.receivedAt) - Date.parse(current.occurredAt);

  return (
    <Dialog
      variant="panel"
      eyebrow={`Event · ${current.category}`}
      title={current.message}
      onClose={onClose}
      footer={
        <>
          <Link className="button button-secondary" to={`/events/${encodeURIComponent(event.id)}`}>
            Open the event
          </Link>
          {canAct && (
            <Button
              variant={current.acknowledged ? 'secondary' : 'primary'}
              busy={acknowledge.isPending}
              busyLabel="Saving…"
              onClick={() => {
                acknowledge.mutate(!current.acknowledged);
              }}
            >
              {current.acknowledged ? 'Reopen' : 'Acknowledge'}
            </Button>
          )}
        </>
      }
    >
      <div className="row">
        <SeverityBadge severity={current.severity} />
        {current.acknowledged ? (
          <span className="badge badge-muted">Acknowledged</span>
        ) : (
          <span className="badge badge-accent">Open</span>
        )}
      </div>

      <Facts
        items={[
          [
            'Server',
            <Link key="server" to={`/servers/${encodeURIComponent(current.serverId)}`}>
              {current.serverName}
            </Link>,
          ],
          [
            'From',
            current.sourceIp === null ? '—' : <AddressLink key="ip" ip={current.sourceIp} />,
          ],
          [
            'When',
            `${formatRelative(current.occurredAt)} · ${formatInstant(current.occurredAt, { timezone })}`,
          ],
          [
            'Reached Bartizo',
            gap > 5 * 60_000
              ? `${formatRelative(current.receivedAt)} (sent late from a buffer)`
              : 'At once',
          ],
          ['Assigned to', current.assignee?.email ?? 'Nobody'],
          ['Done on the server', current.actionTaken ?? 'Nothing'],
        ]}
      />

      {current.path !== null && (
        <section>
          <h3>Path</h3>
          <code className="secret-text">{current.path}</code>
        </section>
      )}

      {detail.data?.note !== undefined && detail.data.note !== null && (
        <section>
          <h3>Note</h3>
          <p className="text-sm">{detail.data.note}</p>
        </section>
      )}

      {detail.data !== undefined && (
        <details>
          <summary className="text-sm">What the source reported</summary>
          <pre className="raw">{JSON.stringify(detail.data.raw, null, 2)}</pre>
        </details>
      )}

      {acknowledge.isError && (
        <Alert tone="danger">
          {acknowledge.error instanceof Error ? acknowledge.error.message : 'Could not save that.'}
        </Alert>
      )}
    </Dialog>
  );
}
